/**
 * GroupLiveJoin — Participant-facing join screen.
 *
 * VER-56 Model A: A participant opens this on their own device, enters the
 * 6-character session code, and is redirected to the (SurveyMonkey) survey URL.
 *
 * This surface is PARTICIPANT-FACING: it must work on phones in a classroom
 * without login. It should eventually live on a public route (e.g.
 * /join or /assess/join) — not behind the admin auth wall.
 *
 * MOCK-FIRST: No backend calls. Entering the hardcoded mock code simulates
 * a successful join. The SurveyMonkey URL is a placeholder.
 *
 * TODOs:
 * - TODO [VER-55]: Replace code validation with GET /api/sessions/:code
 *   (returns session metadata including the SM survey URL for this participant).
 * - TODO [ROUTING]: Move this to a public route — it must NOT require auth.
 *   A separate public-facing app shell (PublicHeader) is already used in
 *   src/core/components/PublicHeader.tsx; follow that pattern.
 * - TODO [VER-55]: On successful validation, open the SurveyMonkey survey URL
 *   in the same tab (or mobile-safe redirect). After SM collector completes,
 *   the SM webhook signals submission back to the backend (VER-55).
 * - TODO [QR]: The QR placeholder should render an actual QR code via a
 *   lightweight library (e.g. qrcode.react) — no new dep without approval.
 */

import React, { useState } from "react";
import { Alert, Box, Button, Card, CardContent, Divider, TextField, Typography } from "@mui/material";
import { QrCode2Outlined, ArrowForwardOutlined, CheckCircleOutlined } from "@mui/icons-material";
import { MOCK_LIVE_SESSION } from "./groupLive.mock";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GroupLiveJoin = (): JSX.Element => {
    const [code, setCode] = useState<string>("");
    const [error, setError] = useState<string>("");
    const [joined, setJoined] = useState<boolean>(false);

    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Uppercase, max 6 chars, alphanumeric only
        const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
        setCode(raw.slice(0, 6));
        if (error.length > 0) {
            setError("");
        }
    };

    const handleJoin = () => {
        if (code.length !== 6) {
            setError("Please enter the full 6-character code.");
            return;
        }
        // TODO [VER-55]: Replace with GET /api/sessions/:code
        if (code !== MOCK_LIVE_SESSION.sessionCode) {
            setError("Session not found. Check the code and try again.");
            return;
        }
        // TODO [VER-55]: Open the SurveyMonkey survey URL returned by the API.
        // window.location.href = data.surveyUrl;
        setJoined(true);
    };

    if (joined) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="60vh"
                p={3}
            >
                <CheckCircleOutlined color="primary" sx={{ fontSize: "4rem", mb: 2 }} />
                <Typography variant="h4" align="center" gutterBottom>
                    You have joined the session
                </Typography>
                <Typography variant="body1" align="center" color="textSecondary" maxWidth={480}>
                    Your wellbeing survey is loading. Please complete it on this device. Your responses are anonymous —
                    they cannot be linked to you individually.
                </Typography>
                {/* TODO [VER-55]: Replace this alert with an actual redirect to the SM survey URL. */}
                <Alert severity="info" sx={{ mt: 3, maxWidth: 480 }}>
                    <strong>Mock only:</strong> In production, your browser would redirect to the WEMWBS survey now.
                </Alert>
            </Box>
        );
    }

    return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" p={3}>
            <Box width={1} maxWidth={440}>
                {/* Header */}
                <Box mb={4} textAlign="center">
                    <Typography variant="h3" color="primary" gutterBottom>
                        Join wellbeing check-in
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                        Enter the session code your practitioner has shared with you.
                    </Typography>
                </Box>

                {/* Session code entry */}
                <Card>
                    <CardContent>
                        <Box mb={3}>
                            <TextField
                                label="Session code"
                                value={code}
                                onChange={handleCodeChange}
                                error={error.length > 0}
                                helperText={error.length > 0 ? error : "6-character code — e.g. WB7X42"}
                                fullWidth
                                inputProps={{
                                    maxLength: 6,
                                    style: {
                                        letterSpacing: "0.4em",
                                        fontWeight: 700,
                                        fontSize: "1.4rem",
                                        textAlign: "center",
                                        textTransform: "uppercase",
                                    },
                                }}
                                placeholder="______"
                            />
                        </Box>

                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            fullWidth
                            endIcon={<ArrowForwardOutlined />}
                            onClick={handleJoin}
                            disabled={code.length !== 6}
                        >
                            Join session
                        </Button>
                    </CardContent>
                </Card>

                {/* QR placeholder */}
                <Divider sx={{ my: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                        or scan
                    </Typography>
                </Divider>

                <Card
                    sx={{
                        border: (theme) => `2px dashed ${theme.palette.borderShade.main}`,
                        textAlign: "center",
                    }}
                >
                    <CardContent>
                        <QrCode2Outlined sx={{ fontSize: "6rem", color: (theme) => theme.palette.borderShade.main }} />
                        <Typography variant="body2" color="textSecondary">
                            QR code placeholder
                        </Typography>
                        <Typography variant="caption" color="textSecondary" display="block" mt={0.5}>
                            {/* TODO [QR]: Render real QR code via qrcode.react once approved. */}
                            TODO [QR]: Scan to open this join page pre-filled with the session code.
                        </Typography>
                    </CardContent>
                </Card>

                {/* Anonymity notice */}
                <Alert severity="success" sx={{ mt: 3 }}>
                    Your responses are anonymous. No one will see your individual answers — only group-level results are
                    shared with your practitioner.
                </Alert>
            </Box>
        </Box>
    );
};

export default GroupLiveJoin;
